import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { apiRequest } from '../lib/api-client';
import { Loading, Panel } from '../ui/Page';
import { importKnowledgeFromFile, importKnowledgeFromForm, importKnowledgeFromPage } from './api';

/**
 * Bringing in what the tenant has already written (Phase 143).
 *
 * The knowledge base could only be typed into, while the answers to their customers' questions
 * were already in the product: a published page, a document they uploaded, the questions their
 * forms ask. Asking somebody to copy and paste those is asking them to maintain two copies.
 *
 * **Form answers are not on this screen**, and that is a decision rather than an omission: a
 * person's submitted answers are erased when they ask to be forgotten, and a copy inside a
 * knowledge source is a copy the erasure cannot reach. What a form contributes here is the
 * tenant's own writing — the questions.
 */

type Kind = 'page' | 'file' | 'form';

interface Option {
  id: string;
  label: string;
  hint?: string;
}

export function ImportKnowledge({ onDone }: { onDone: () => void }): React.JSX.Element {
  const client = useQueryClient();
  const [kind, setKind] = useState<Kind>('page');
  const [error, setError] = useState<string | null>(null);

  const options = useQuery({
    queryKey: ['knowledge-import', kind],
    queryFn: async (): Promise<Option[]> => {
      if (kind === 'page') {
        const result = await apiRequest<{
          pages: { id: string; name: string; status: string }[];
        }>('/v1/pages?limit=100');

        // Only what customers are actually reading: a draft is not an answer to anything.
        return result.pages
          .filter((page) => page.status === 'PUBLISHED')
          .map((page) => ({ id: page.id, label: page.name }));
      }

      if (kind === 'form') {
        const result = await apiRequest<{ forms: { id: string; name: string }[] }>(
          '/v1/forms?limit=100',
        );

        return result.forms.map((form) => ({ id: form.id, label: form.name }));
      }

      const result = await apiRequest<{
        files: { id: string; name: string; mimeType: string }[];
      }>('/v1/files?limit=200');

      // The types an extractor exists for. A PDF needs a library this build does not have, and
      // indexing its bytes would store noise the search would then rank.
      const readable = ['text/plain', 'text/markdown', 'text/csv', 'application/json', 'text/html'];

      return result.files
        .filter((file) => readable.includes(file.mimeType.split(';')[0]!.trim().toLowerCase()))
        .map((file) => ({ id: file.id, label: file.name, hint: file.mimeType }));
    },
  });

  const bring = useMutation({
    mutationFn: (id: string) =>
      kind === 'page'
        ? importKnowledgeFromPage(id)
        : kind === 'form'
          ? importKnowledgeFromForm(id)
          : importKnowledgeFromFile(id),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['knowledge'] });
      onDone();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <Panel title={t('ai.import.title')} description={t('ai.import.description')}>
      <div className="chips" role="tablist">
        {(['page', 'file', 'form'] as Kind[]).map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={kind === option}
            className={`chip${kind === option ? ' chip--active' : ''}`}
            onClick={() => {
              setKind(option);
              setError(null);
            }}
          >
            {t(`ai.import.kind.${option}`)}
          </button>
        ))}
      </div>

      {kind === 'form' && <p className="muted small">{t('ai.import.formNote')}</p>}
      {kind === 'file' && <p className="muted small">{t('ai.import.fileNote')}</p>}

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {options.isLoading ? (
        <Loading />
      ) : (options.data ?? []).length === 0 ? (
        <p className="muted">{t('ai.import.empty')}</p>
      ) : (
        <ul className="import-list">
          {(options.data ?? []).map((option) => (
            <li key={option.id}>
              <span className="cell-stack">
                <span className="strong">{option.label}</span>
                {option.hint && <span className="muted small">{option.hint}</span>}
              </span>

              <button
                type="button"
                className="button button--quiet button--compact"
                disabled={bring.isPending}
                onClick={() => bring.mutate(option.id)}
              >
                {t('ai.import.bring')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
